// pages/MspActivitiesSection.jsx
import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Grid,
  Stack,
  Typography,
  Chip,
  ClickAwayListener,
  Grow,
  IconButton,
  Paper,
  Popper,
  Button,
  CircularProgress,
  Tooltip,
  Divider,
  Popover,
  Checkbox,
} from "@mui/material";
import NestedTableComponent from "components/NestedTableComponent";
import { useParams } from "react-router-dom";
import moment from "moment";
import ArrowDropUpRoundedIcon from "@mui/icons-material/ArrowDropUpRounded";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import WbsTreeView from "view/dashboard/wbsTreeView";
import WbsDateFilters from "components/WbsDateFilters";
import { useGetScheduleTrackerQuery, useLazyGetScheduleTrackerQuery } from "store/slices/apis/scheduleSlice";

import { PiArrowFatRightFill, PiArrowFatUpFill, PiArrowFatDownFill } from "react-icons/pi";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ScheduleWeeklySection from "view/baselineSchedule/ScheduleWeeklySection";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useGetLastUpdatedOnStatusQuery } from "store/slices/apis/projectStatusApiSlice";
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { safeDivide } from "utils/numberUtils";

// This is your flag – can be prop/env/feature flag
const LOAD_MSP_SCHEDULE = true; // change to false to hide

const validationSchema = Yup.object({
  wbsId: Yup.string().nullable(),
  wbsName: Yup.string().nullable(),
});

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  return Number(value).toLocaleString("en-IN");
};

const formatKey = (str) => String(str || "").toLowerCase().replace(/[^a-zA-Z0-9]/g, "");
const formatDate = (date) => {
  if (!date) return "";
  const momentDate = moment(date);
  return momentDate.isValid() ? momentDate.format("DD/MM/YYYY") : "";
};

const abbreviations = [
  // { short: "PV", full: "Planned value", color: "#00344C" },
  // { short: "AC", full: "Actual cost", color: "#00344C" },
  // { short: "BAC", full: "Budget at completion", color: "#00344C" },
  // { short: "BCWS", full: "Budgeted cost of work scheduled", color: "#00344C" },
  // { short: "EV", full: "Earned value", color: "#00344C" },
  // { short: "CV", full: "Cost variance", color: "#00344C" },
  // { short: "EAC", full: "Estimated at completion", color: "#00344C" },
  { short: "BCWP", full: "Budgeted cost of work performed", color: "#00344C" },
  // { short: "SV", full: "Schedule variance", color: "#00344C" },
  // { short: "CPI", full: "Cost performance index", color: "#00344C" },
  // { short: "ETC", full: "Estimated cost to complete", color: "#00344C" },
  // { short: "ACWP", full: "Actual cost of work performed", color: "#00344C" },
  // { short: "SPI", full: "Schedule performance index", color: "#00344C" },

  // { short: "HVAC", full: "Heating Ventilation and Air Conditioning", color: "#2563eb" },
  // { short: "BOQ", full: "Bill of Quantity", color: "#16a34a" },
  // { short: "MEP", full: "Mechanical Electrical Plumbing", color: "#f59e0b" },
  // { short: "BMS", full: "Building Management System", color: "#9333ea" },
  // { short: "ELV", full: "Extra Low Voltage", color: "#ef4444" }
];

const Header = ({ title, subtitle, align = "center" }) => (
  <Box
    sx={{
      width: "100%",
      textAlign: align,
      display: "flex",
      flexDirection: "column",
      lineHeight: 1.1,
    }}
  >
    <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
      {title}
    </Typography>
    {subtitle && (
      <Typography sx={{ fontSize: 11, fontWeight: 500, opacity: 0.8 }}>
        {subtitle}
      </Typography>
    )}
  </Box>
);

const parseMonthYear = (monthStr) => {
    const [mon, year] = monthStr.split("-");
    const date = new Date(`${mon} 1, ${year}`);
    return date;
};

const transformApiData = (apiData) => {
  return apiData?.map((item) => {
    let flattenedRow = {
      id: item.activityId,
      activity: item.activity,
      baselineStart: formatDate(item.baselineStart),
      baselineFinish: formatDate(item.baselineFinish),
      projectCompletion: item.projectCompletion,
      plannedComplete: item.plannedComplete,
      parentTaskId: item.parentTaskId,
      estimateAtCompletion: item.estimateAtCompletion,
      actualStart: formatDate(item.actualStart),
      actualFinish: formatDate(item.actualFinish),
      anticipatedStart: formatDate(item.anticipatedStart),
      anticipatedFinish: formatDate(item.anticipatedFinish),
      remainingDurationAsPerBaseline: item.remainingDurationAsPerBaseline,
      remainingDurationAsPerFinish: item.remainingDurationAsPerFinish,
      remaining: item.remaining,
      projectedSum: item.projectedSum,
      requiredRateAsPerBaseline: item.requiredRate,

      // cost summary (if present)
      baselineCostTotal: item.baselineCostTotal,
      revisedBaselineCostTotal: item.revisedBaselineCostTotal,
      estimateAtCompletion: item.estimateAtCompletion,
      actualCostTotal: item.actualCostTotal,
      evTotal: item.evTotal,
      resources: item.resources ?? [],
      isLeaf: item.isLeaf,
      remarks: item?.remarks
    };

    const cutoffDate = new Date();
    cutoffDate.setMonth(cutoffDate.getMonth() + 3);

    item.months?.forEach((m) => {
      const mKey = formatKey(m.month);
      const monthDate = parseMonthYear(m.month);

      flattenedRow[`${mKey}BaselineCost_Month`] = m.baselineCost_Month;
      flattenedRow[`${mKey}BaselineUnits_Month`] = m.baselineUnits_Month ? m.baselineUnits_Month : m.baselineCost_Month;
      flattenedRow[`${mKey}BaselineUnits_Cum`] = m.baselineUnits_Cum ? m.baselineUnits_Cum : m.baselineCost_Month;;
      flattenedRow[`${mKey}ActualCost_Month`] = m.actualCost_Month;
      //flattenedRow[`${mKey}BaselinePlanPct_Month`] = ((m.baselineUnits_Month * 100) / item?.revisedBaselineCostTotal)?.toFixed(2);
      flattenedRow[`${mKey}BaselinePlanPct_Month`] = safeDivide(
          m.baselineUnits_Month,
          item?.revisedBaselineCostTotal,
          { multiplyBy: 100, decimals: 2 }
      );
      //flattenedRow[`${mKey}BaselinePlanPct_Month`] = m.baselinePlanPct_Month;
      flattenedRow[`${mKey}ProjectedCompletionPct_Month`] = m.projectedCompletionPct_Month;
      flattenedRow[`${mKey}RequiredTarget_Month`] = m.requiredTarget_Month;
      flattenedRow[`${mKey}ActualPct_TillStatus`] = m.actualPct_TillStatus;
      flattenedRow[`${mKey}CurrentRatePerDay`] = m.currentRatePerDay;
      flattenedRow[`${mKey}RequiredRatePerDay`] = m.requiredRatePerDay;
      flattenedRow[`${mKey}RequiredTargetCost_Month`] = monthDate > cutoffDate ? null :m.requiredTargetCost_Month;
      flattenedRow[`${mKey}IsLeaf`] = m.isLeaf;
      flattenedRow.hasChildren = !flattenedRow.isLeaf;
    });

    flattenedRow.children = [];
    flattenedRow.hasChildren = true;
    return flattenedRow;
  });
};

const ProgressBar = ({ value, color = "#1976d2" }) => {
  const percent = Math.min(Math.max(parseFloat(value) || 0, 0), 100);
  const textOnFill = percent >= 55;

  return (
    <Box sx={{ width: "100%", py: 0.75, px: 0.25 }}>
      <Box
        sx={{
          height: 18,
          bgcolor: "#F9FAFB",
          borderRadius: 1,
          overflow: "hidden",
          position: "relative",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            width: `${percent}%`,
            height: "100%",
            bgcolor: color,
            transition: "width 0.5s ease-in-out",
          }}
        />
        <Typography
          variant="caption"
          sx={{
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
            fontSize: "11px",
            fontWeight: 700,
            lineHeight: 1,
            whiteSpace: "nowrap",
            pointerEvents: "none",
            userSelect: "none",
            left: textOnFill ? 0 : `calc(${percent}% + 6px)`,
            right: textOnFill ? `calc(100% - ${percent}% + 6px)` : "auto",
            textAlign: textOnFill ? "right" : "left",
            color: textOnFill ? "#FFFFFF" : "#111827",
          }}
        >
          {percent}%
        </Typography>
      </Box>
    </Box>
  );
};

const getProjectionCompletionColor = (planned, actual) => {
  if (actual > planned) return "#1A932E";
  if (planned === actual) return "#1A932E";
  return "#FF383C";
};

const getBaseLineArrow = (baseline, required) => {
  const b = Number(baseline);
  const r = Number(required);
  if (Number.isFinite(r) && Number.isFinite(b) && r > b) return <PiArrowFatUpFill color="#E65F2B" />;
  if (Number.isFinite(r) && Number.isFinite(b) && r === b) return <PiArrowFatRightFill color="#E76E01" />;
  return <PiArrowFatDownFill color="#1A932E" />;
};

const filterLabels = {
  wbsName: "WBS Name",
  startDateFrom: "Start From",
  startDateTo: "Start To",
  endDateFrom: "End From",
  endDateTo: "End To",
};

const labelTextStyles = {
  fontFamily: "Outfit",
  fontWeight: 500,
  fontSize: "16px",
  lineHeight: "100%",
  letterSpacing: "0px",
  mb: 1,
};

// ---- Weekly helpers (DEMO for tomorrow; replace with API later) ----
const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// ---- TEMP HARDCODE (for demo) ----
const HARDCODED_WEEKLY = {
  "Jan-2026": [
    {
      label: "29 Dec – 04 Jan",
      BaselineCost_Month: "6.93",
      ActualCost_Month: "7.68",
      BaselinePlanPct_Month: "1.8",
      ProjectedCompletionPct_Month: "6",
      RequiredTarget_Month: "4.8",
      ActualPct_TillStatus: "2",
      CurrentRatePerDay: "1",
      RequiredRatePerDay: "1",
    },
    {
      label: "05 Jan – 11 Jan",
      BaselineCost_Month: "6.93",
      ActualCost_Month: "",
      BaselinePlanPct_Month: "1.8",
      ProjectedCompletionPct_Month: "6",
      RequiredTarget_Month: "4.8",
      ActualPct_TillStatus: "",
      CurrentRatePerDay: "",
      RequiredRatePerDay: "",
    },
    {
      label: "12 Jan – 18 Jan",
      BaselineCost_Month: "6.93",
      ActualCost_Month: "",
      BaselinePlanPct_Month: "1.8",
      ProjectedCompletionPct_Month: "6",
      RequiredTarget_Month: "4.8",
      ActualPct_TillStatus: "",
      CurrentRatePerDay: "",
      RequiredRatePerDay: "",
    },
    {
      label: "19 Jan – 25 Jan",
      BaselineCost_Month: "6.93",
      ActualCost_Month: "",
      BaselinePlanPct_Month: "1.8",
      ProjectedCompletionPct_Month: "6",
      RequiredTarget_Month: "4.8",
      ActualPct_TillStatus: "",
      CurrentRatePerDay: "",
      RequiredRatePerDay: "",
    },
    {
      label: "26 Jan – 01 Feb",
      BaselineCost_Month: "6.93",
      ActualCost_Month: "",
      BaselinePlanPct_Month: "1.8",
      ProjectedCompletionPct_Month: "6",
      RequiredTarget_Month: "4.8",
      ActualPct_TillStatus: "",
      CurrentRatePerDay: "",
      RequiredRatePerDay: "",
    },
  ],

  "Feb-2026": [
    {
      label: "03 Feb – 09 Feb",
      BaselineCost_Month: "90",
      ActualCost_Month: "0",
      BaselinePlanPct_Month: "7.50",
      ProjectedCompletionPct_Month: "",
      RequiredTarget_Month: "0",
      ActualPct_TillStatus: "",
      CurrentRatePerDay: "",
      RequiredRatePerDay: "",
    },
    {
      label: "10 Feb – 16 Feb",
      BaselineCost_Month: "95",
      ActualCost_Month: "0",
      BaselinePlanPct_Month: "7.50",
      ProjectedCompletionPct_Month: "",
      RequiredTarget_Month: "0",
      ActualPct_TillStatus: "",
      CurrentRatePerDay: "",
      RequiredRatePerDay: "",
    },
    {
      label: "17 Feb – 23 Feb",
      BaselineCost_Month: "100",
      ActualCost_Month: "0",
      BaselinePlanPct_Month: "7.50",
      ProjectedCompletionPct_Month: "",
      RequiredTarget_Month: "0",
      ActualPct_TillStatus: "",
      CurrentRatePerDay: "",
      RequiredRatePerDay: "",
    },
    {
      label: "24 Feb – 02 Mar",
      BaselineCost_Month: "92",
      ActualCost_Month: "0",
      BaselinePlanPct_Month: "7.50",
      ProjectedCompletionPct_Month: "",
      RequiredTarget_Month: "0",
      ActualPct_TillStatus: "",
      CurrentRatePerDay: "",
      RequiredRatePerDay: "",
    },
  ],
};
// -------------------------------

const formatWeekLabel = (start, end) => `${moment(start).format("DD MMM")} – ${moment(end).format("DD MMM")}`;

const splitMonthIntoWeeks = (monthStart, monthEnd) => {
  let start = moment(monthStart).startOf("day");
  const end = moment(monthEnd).startOf("day");
  const weeks = [];

  while (start.isSameOrBefore(end)) {
    const weekStart = start.clone();
    const weekEnd = start.clone().endOf("isoWeek"); // Sunday
    const clippedEnd = moment.min(weekEnd, end);

    weeks.push({
      weekStart: weekStart.toDate(),
      weekEnd: clippedEnd.toDate(),
      days: clippedEnd.diff(weekStart, "days") + 1,
    });

    start = clippedEnd.clone().add(1, "day");
  }
  return weeks;
};

const buildFakeWeeklySeries = (monthStart, monthEnd, planMonthEndPct, actualMonthEndPct) => {
  const weeks = splitMonthIntoWeeks(monthStart, monthEnd);
  const totalDays = weeks.reduce((s, w) => s + w.days, 0) || 1;

  let planCum = 0;
  let actualCum = 0;

  const overrideWeekIndex = weeks.length >= 2 ? 1 : -1; // show 1 override in demo

  return weeks.map((w, idx) => {
    // const planAdd = planMonthEndPct * (w.days / totalDays);
    // const actualAddRaw = actualMonthEndPct * (w.days / totalDays);

    // const lagFactor = idx === 0 ? 0.85 : idx === 1 ? 0.92 : 1.0;
    // const actualAdd = actualAddRaw * lagFactor;

    // const prevActual = actualCum;

    // planCum += planAdd;
    // actualCum += actualAdd;

    // const hasOverride = idx === overrideWeekIndex;
    // const overriddenActualCum = hasOverride ? Math.max(actualCum, planCum - 0.5) : actualCum;
    // const actualCumFinal = hasOverride ? overriddenActualCum : actualCum;

    return {
      label: formatWeekLabel(w.weekStart, w.weekEnd),
      BaselineCost_Month: "101",
      ActualCost_Month: "120",
      BaselinePlanPct_Month: "98",
      ProjectedCompletionPct_Month: "55",
      RequiredTarget_Month: "68",
      ActualPct_TillStatus: "72",
      CurrentRatePerDay: "50",
      RequiredRatePerDay: "10",
      // source: hasOverride ? "Override" : "Computed",
      // hasOverride,
    };
  });
};
// ---------------------------------------------------------------

const AbbreviationBadge = ({
  short,
  full,
  color = "#2563eb",
  expandable = true
}) => {

  return (
    <Box
      sx={{
        backgroundColor: "#F8FAFC",
        border: "1px solid #E2E8F0",
        borderRadius: "3px",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        // transition: "all 0.2s ease",
        // "&:hover": {
        //     boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
        //     transform: "translateY(-1px)"
        // }
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          // cursor: expandable ? "pointer" : "default"
        }}
      >
        <Box
          sx={{
            px: 1.25,
            py: 0.65,
            display: "flex",
            alignItems: "center",
            gap: 1,
            flex: 1
          }}
        >
          <Tooltip title={full} arrow>
            <Typography fontWeight={700} fontSize={13}>
              {short}
            </Typography>
          </Tooltip>

          <Typography fontSize={12} color="text.secondary" noWrap>
            {full}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

const ScheduleActivitiesSection = ({ showMspSchedule = LOAD_MSP_SCHEDULE, isShowMonths = false, exportButton, columnFiltering = false, showRemarksColumn = false }) => {
  const { projectId } = useParams();
  const [initialData, setInitialData] = useState([]);

  const [trigger, { data: lazyData, isFetching: lazyLoading, isError: lazyError }] =
    useLazyGetScheduleTrackerQuery();

  const { data, isFetching: isLoading, isError } = useGetScheduleTrackerQuery({ projectId });

  const [loaderId, setLoaderId] = useState(null);
  const [isFiltering, setIsFiltering] = useState(false);
  const [isApplyFilter, setIsApplyFIlter] = React.useState(false);

  const [open, setOpen] = useState(false);
  const anchorRef = React.useRef(null);
  const arrowRef = React.useRef(null);

  const [showDefination, setShowDefination] = useState(false);
  const { data: scheduleDate } = useGetLastUpdatedOnStatusQuery({ ProjectId: projectId });
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [columnAnchor, setColumnAnchor] = useState(null);
  const [visibleMonths, setVisibleMonths] = useState([])

  const handleColumnOpen = (e) => setColumnAnchor(e.currentTarget);
  const handleColumnClose = () => setColumnAnchor(null);

  const [filters, setFilters] = useState({
    projectId: projectId,
    wbsName: "",
    parentId: "",
    startDateFrom: "",
    startDateTo: "",
    endDateFrom: "",
    endDateTo: "",
  });

  // Weekly popover state (Option A drilldown demo)
  const [weekPopover, setWeekPopover] = useState({
    open: false,
    anchorEl: null,
    row: null,
    monthMeta: null,
    mKey: null,
  });

  const openWeekPopover = (event, row, monthMeta, mKey) => {
    event.stopPropagation();

    setWeekPopover({ open: true, anchorEl: event.currentTarget, row, monthMeta, mKey });
  };

  const closeWeekPopover = () => {
    setWeekPopover({ open: false, anchorEl: null, row: null, monthMeta: null, mKey: null });
  };

  const [resourcePopover, setResourcePopover] = useState({
    open: false,
    anchorEl: null,
    row: null,
  });

  const openResourcePopover = (event, row) => {
    event.stopPropagation();
    setResourcePopover({ open: true, anchorEl: event.currentTarget, row });
  };

  const closeResourcePopover = () => {
    setResourcePopover({ open: false, anchorEl: null, row: null });
  };

  const months = useMemo(() => {
    if (!data?.data?.[0]?.months) return [];
    return data.data[0].months.map((m) => ({
      month: m.month,
      monthEnd: m.monthEnd,
      monthStart: m.monthStart,
      monthKey: formatKey(m.month),
      //span: m.month === "Nov-2024 to Dec-2025" ? 4 : 10,
    }));
  }, [data]);

  const tableColumns = useMemo(() => {
    const cols = [
      {
        name: <Header title="Res." />,
        field: "resources",
        width: 110,
        sticky: true,
        align: "center",
        renderer: (row) => {
          if (row?.resources?.length == 0) return null;
          const count = row?.resources?.length || 0;
          const disabled = !row?.isLeaf;
          return (
            <Tooltip title={disabled ? "No resources" : `${count} resource(s)`}>
              <span>
                <IconButton
                  size="small"
                  disabled={disabled}
                  onClick={(e) => openResourcePopover(e, row)}
                  sx={{
                    p: 0.5,
                    bgcolor: disabled ? "transparent" : "rgba(16,185,129,0.08)",
                    borderRadius: 1,
                    "&:hover": { bgcolor: "rgba(16,185,129,0.14)" },
                  }}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 16, color: disabled ? "#9CA3AF" : "#059669" }} />
                </IconButton>
              </span>
            </Tooltip>
          );
        },
      },
      {
        name: <Header title="Activity" align='left' />,
        field: "activity",
        sticky: true,
        width: 400,
        align: "left",
        renderer: (row) => row?.activity ?? "N/A",
      },
      {
        name: <Header title="Baseline Planned Capex" subtitle="(INR Cr)" />,
        field: "baselineCostTotal",
        sticky: true,
        width: 80,
        renderer: (row) => row?.baselineCostTotal ?? "N/A",
      },
      {
        name: <Header title="Revised Baseline Planned Capex" subtitle="(INR Cr)" />,
        // name: <Header title="BAC" subtitle="(INR Cr)" />,
        field: "revisedBaselineCostTotal",
        sticky: true,
        width: 100,
        renderer: (row) => row?.revisedBaselineCostTotal ?? "N/A",
      },
      {
        // name: <Header title="Actual Capex" subtitle="(INR Cr)" />,
        name: <Header title="BCWP" subtitle="(INR Cr)" />,
        field: "actualCostTotal",
        sticky: true,
        width: 80,
        renderer: (row) => row.evTotal == null || row.evTotal == 0 ? row?.actualCostTotal ?? "N/A" : row.evTotal,
      },
      { name: <Header title="Baseline Start" />, field: "baselineStart", width: 80, renderer: (row) => (row?.baselineStart ? row.baselineStart : "N/A") },
      { name: <Header title="Baseline Finish" />, field: "baselineFinish", width: 80, renderer: (row) => (row?.baselineFinish ? row.baselineFinish : "N/A") },
      { name: <Header title="Actual Start" />, field: "actualStart", width: 80, renderer: (row) => (row?.actualStart ? row.actualStart : "N/A") },
      { name: <Header title="Actual Finish" />, field: "actualFinish", width: 80, renderer: (row) => (row?.actualFinish ? row.actualFinish : "N/A") },
      { name: <Header title="Actual / Anticipated Start" />, field: "anticipatedStart", width: 80, renderer: (row) => (row?.anticipatedStart ? row.anticipatedStart : "N/A") },
      { name: <Header title="Actual / Anticipated Finish" />, field: "anticipatedFinish", width: 80, renderer: (row) => (row?.anticipatedFinish ? row.anticipatedFinish : "N/A") },
      {
        name: <Header title="Remaining Duration as per Baseline" subtitle="(Days)" />,
        width: 100,
        field: "remainingDurationAsPerBaseline",
        renderer: (row) => row?.remainingDurationAsPerBaseline ?? "N/A",
      },
      {
        name: <Header title="Remaining Duration as per Finish" subtitle="(Days)" />,
        width: 100,
        field: "remainingDurationAsPerFinish",
        renderer: (row) => row?.remainingDurationAsPerFinish ?? "N/A",
      },
      {
        name: <Header title="Planned Complete" subtitle="(Percentage)" />,
        field: "plannedComplete",
        width: 90,
        padding: 0,
        renderer: (row) => <ProgressBar value={row.plannedComplete?.toFixed(2)} color="#007DD6" />,
      },
      {
        name: <Header title="Project Completion" subtitle="(Percentage)" />,
        field: "projectCompletion",
        width: 90,
        padding: 0,
        renderer: (row) => (
          <ProgressBar
            value={row.projectCompletion?.toFixed(2)}
            color={() => getProjectionCompletionColor(row.plannedComplete, row.projectCompletion)}
          />
        ),
      },
      {
        name: <Header title="Remaining" subtitle="(Percentage)" />,
        field: "remaining",
        width: 80,
        renderer: (row) =>
          row?.remaining !== null && row?.remaining !== undefined ? `${row.remaining}%` : "N/A",
      },

    ];
    if (showRemarksColumn) {
      cols.push({
        name: <Header title="Remarks" />,
        field: "remarks",
        width: 400,
        align: "center",
        renderer: (row) => row?.remarks ?? "",
      })
    }

    if (isShowMonths) {
      months.forEach((m) => {
        const mKey = m?.monthKey ? m.monthKey : formatKey(m.month);

        if (m.month === "Nov-2024 to Dec-2025") {
          cols.push(
            { name: <Header title="BaseLine Planned Capex" subtitle="(INR Cr)" />, field: `${mKey}BaselineUnits_Cum`, type: "month", width: 80, renderer: (row) => row?.[`${mKey}BaselineUnits_Cum`] ?? "N/A", monthKey: mKey },
            //  { name: <Header title="BAC" subtitle="(INR Cr)" />, field: `${mKey}BaselineCost_Month`, type: "month", width: 80, renderer: (row) => row?.[`${mKey}BaselineCost_Month`] ?? "N/A" },
            // { name: <Header title="Actual Capex" subtitle="(INR Cr)" />, field: `${mKey}ActualCost_Month`, type: "month", width: 80, renderer: (row) => row?.[`${mKey}ActualCost_Month`] ?? "N/A" },
            { name: <Header title="BCWP" subtitle="(INR Cr)" />, field: `${mKey}ActualCost_Month`, type: "month", width: 80, renderer: (row) => row?.[`${mKey}ActualCost_Month`] ?? "N/A", monthKey: mKey },
            { name: <Header title="Baseline Plan" subtitle="(Percentage)" />, field: `${mKey}BaselinePlanPct_Month`, type: "month", width: 80, renderer: (row) => row?.[`${mKey}BaselinePlanPct_Month`] ?? "N/A", monthKey: mKey },
            // { name: <Header title="BAC" subtitle="(Percentage)" />, field: `${mKey}BaselinePlanPct_Month`, type: "month", width: 80, renderer: (row) => row?.[`${mKey}BaselinePlanPct_Month`] ?? "N/A" },
            {
              // name: <Header title="Actual" subtitle="(Percentage)" />,
              name: <Header title="BCWP" subtitle="(Percentage)" />,
              field: `${mKey}ActualPct_TillStatus`,
              type: "month",
              width: 90,
              renderer: (row) => row?.[`${mKey}ActualPct_TillStatus`] ?? "N/A",
              monthKey: mKey
            }
          );
        } else {
          cols.push(
            {
              name: <Header title="Weekly" />,
              field: `${mKey}Weekly`,
              type: "month",
              width: 50,
              align: "center",
              monthKey: mKey,
              renderer: (row) => (
                <Tooltip title={`Weekly breakdown (${m.month})`}>
                  <IconButton
                    size="small"
                    onClick={(e) => openWeekPopover(e, row, m, mKey)}
                    sx={{
                      p: 0.5,
                      bgcolor: "rgba(14,115,246,0.06)",
                      borderRadius: 1,
                      "&:hover": { bgcolor: "rgba(14,115,246,0.12)" },
                    }}
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 16, color: "#0E73F6" }} />
                  </IconButton>
                </Tooltip>
              ),
            },
            { name: <Header title="Baseline Planned Capex" subtitle="(INR Cr)" />, field: `${mKey}BaselineUnits_Month`, type: "month", width: 80, renderer: (row) => row?.[`${mKey}BaselineUnits_Month`] ?? "N/A", monthKey: mKey },
            //{ name: <Header title="BAC" subtitle="(INR Cr)" />, field: `${mKey}BaselineCost_Month`, type: "month", width: 80, renderer: (row) => row?.[`${mKey}BaselineCost_Month`] ?? "N/A" },
            // { name: <Header title="Actual Capex" subtitle="(INR Cr)" />, field: `${mKey}ActualCost_Month`, type: "month", width: 80, renderer: (row) => row?.[`${mKey}ActualCost_Month`] ?? "N/A" },
            { name: <Header title="BCWP" subtitle="(INR Cr)" />, field: `${mKey}ActualCost_Month`, type: "month", width: 80, renderer: (row) => row?.[`${mKey}ActualCost_Month`] ?? "N/A", monthKey: mKey },
            {
              // name: <Header title="Revised Target Capex for the month" subtitle="(INR Cr)" />,
              name: <Header title="Revised Target for the month" subtitle="(INR Cr)" />,
              field: `${mKey}RequiredTargetCost_Month`,
              type: "month",
              width: 90,
              monthKey: mKey,
              renderer: (row) => {
                const val = row[`${mKey}RequiredTargetCost_Month`];
                return (
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 0.5 }}>
                    {getBaseLineArrow(row?.[`${mKey}BaselineCost_Month`], row?.[`${mKey}RequiredTargetCost_Month`])}
                    {/* <Typography sx={{ fontSize: "12px" }}>{val.toFixed(2)}</Typography> */}
                    <Typography sx={{ fontSize: "12px" }}>{Number.isFinite(val) ? val.toFixed(2) : "N/A"}</Typography>
                  </Box>
                );
              },
            },
            { name: <Header title="Baseline Plan" subtitle="(Percentage)" />, field: `${mKey}BaselinePlanPct_Month`, type: "month", width: 80, renderer: (row) => row?.[`${mKey}BaselinePlanPct_Month`] ?? "N/A", monthKey: mKey },
            // { name: <Header title="BAC" subtitle="(Percentage)" />, field: `${mKey}BaselinePlanPct_Month`, type: "month", width: 80, renderer: (row) => row?.[`${mKey}BaselinePlanPct_Month`] ?? "N/A" },
            { name: <Header title="Projected For the month" subtitle="(Percentage)" />, field: `${mKey}ProjectedCompletionPct_Month`, type: "month", width: 80, renderer: (row) => row?.[`${mKey}ProjectedCompletionPct_Month`] ?? "N/A", monthKey: mKey },
            {
              name: <Header title="Revised Target for the month" subtitle="(Percentage)" />,
              field: `${mKey}RequiredTarget_Month`,
              type: "month",
              width: 90,
              monthKey: mKey,
              renderer: (row) => {
                const val = parseFloat(row[`${mKey}RequiredTarget_Month`]) || 0;
                return (
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 0.5 }}>
                    {getBaseLineArrow(row?.[`${mKey}BaselinePlanPct_Month`], row?.[`${mKey}RequiredTarget_Month`])}
                    <Typography sx={{ fontSize: "12px" }}>{val.toFixed(2)}</Typography>
                  </Box>
                );
              },
            },
            {
              // name: <Header title="Actual till date" subtitle="(Percentage)" />,
              name: <Header title="BCWP till date" subtitle="(Percentage)" />,
              field: `${mKey}ActualPct_TillStatus`,
              type: "month",
              width: 90,
              monthKey: mKey,
              renderer: (row) => row?.[`${mKey}ActualPct_TillStatus`] ?? "N/A"
            },
            { name: <Header title="Current Rate per day" subtitle="(Percentage)" />, field: `${mKey}CurrentRatePerDay`, type: "month", width: 100, renderer: (row) => row?.[`${mKey}CurrentRatePerDay`] ?? "N/A", monthKey: mKey },
            { name: <Header title="Required Rate per day to achieve baseline" subtitle="(Percentage)" />, field: `${mKey}RequiredRatePerDay`, type: "month", width: 80, renderer: (row) => row?.[`${mKey}RequiredRatePerDay`] ?? "N/A", monthKey: mKey }
          );
        }
      });
    }

    return cols;
  }, [months, isShowMonths]);

  const monthsWithSpan = useMemo(() => {
    return months.map((m) => {
      const mKey = m.monthKey || formatKey(m.month);

      const span = tableColumns.filter(
        (col) =>
          col.type === "month" &&
          col.field.startsWith(mKey)
      ).length;

      return {
        ...m,
        span: visibleMonths.includes(mKey) ? span : 0
      }
    }).filter(item => item.span);
  }, [months, tableColumns, visibleMonths]);
  console.log("monthsWithSpan", monthsWithSpan)

  useEffect(() => {
    if (data?.data) {
      const transformedData = transformApiData(data.data);
      console.log(transformedData);
      setInitialData(transformedData);
    }
  }, [data]);



  const insertChildByParentId = (prevData, parentId, newData) => {
    return prevData.map((item) => {
      if (item.id === parentId) {
        return { ...item, children: newData, hasChildren: newData?.length > 0 ? true : false };
      }
      if (item.children?.length > 0) {
        return { ...item, children: insertChildByParentId(item.children, parentId, newData) };
      }
      return item;
    });
  };

  const handleClick = async (row) => {
    if (row.children.length > 0 || row.isLeaf == true) return;

    try {
      setIsFiltering(false);
      const parentId = row.id;
      setLoaderId(parentId);
      const result = await trigger({ projectId: projectId, parentId: parentId, showMonths: isShowMonths, }).unwrap();
      const childrens = transformApiData(result?.data) || [];
      const updatedData = insertChildByParentId(initialData, parentId, childrens);
      setInitialData(updatedData);
    } catch (error) {
      console.log("Lazy Load Error:", error);
    } finally {
      setLoaderId(null);
    }
  };

  const handleApplyFilters = async (values, { setSubmitting }) => {
    setIsFiltering(true);

    const payload = {
      projectId: projectId,
      activityId: values.wbsId || "",
      startDateFrom: values.startDateFrom || "",
      startDateTo: values.startDateTo || "",
      endDateFrom: values.endDateFrom || "",
      endDateTo: values.endDateTo || "",
    };

    const newFilters = { ...payload, wbsName: values.wbsName || "" };
    setFilters(newFilters);

    await trigger(payload);

    setIsApplyFIlter(true);
    setOpen(false);
    setSubmitting(false);
  };

  const handleReset = (resetForm) => {
    resetForm();
    resetAllFilters();
  };

  const resetAllFilters = () => {
    setIsFiltering(true);
    const resetValues = {
      projectId,
      wbsId: "",
      wbsName: "",
      activityId: "",
      startDateFrom: "",
      startDateTo: "",
      endDateFrom: "",
      endDateTo: "",
    };

    setFilters(resetValues);
    trigger(resetValues);

    setIsApplyFIlter(false);
    setOpen(false);
  };

  const handleToggle = () => setOpen((prevOpen) => !prevOpen);

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) return;
    setOpen(false);
  };

  useEffect(() => {
    if (lazyData?.data && isFiltering) {
      const transformedFilteredData = transformApiData(lazyData.data);
      setInitialData(transformedFilteredData);
      setIsFiltering(false);
    }
  }, [lazyData, isFiltering]);

  const handleFilterDelete = async (key) => {
    if (key === "wbsName") {
      resetAllFilters();
      return;
    }
    setIsFiltering(true);
    const updatedFilters = { ...filters, [key]: "" };
    setFilters(updatedFilters);
    const { wbsName, ...apiPayload } = updatedFilters;
    await trigger(apiPayload);
  };

  const handleToggleDefination = () => {
    setShowDefination((prev) => !prev);
  }

  // File Export

  const handleExport = () => {
    if (!initialData || initialData.length === 0) return;

    const flattenRows = (rows) => {
      let result = [];
      rows.forEach((row) => {
        result.push(row);
        if (row.children && row.children.length > 0) {
          result = result.concat(flattenRows(row.children));
        }
      });
      return result;
    };

    const flatData = flattenRows(initialData);

    const staticHeaders = [
      // "Res.",
      "Activity",
      "Baseline Planned Capex (INR Cr)",
      "Revised Baseline Planned Capex (INR Cr)",
      "BCWP (INR Cr)",
      "Baseline Start",
      "Baseline Finish",
      "Actual Start",
      "Actual Finish",
      "Actual / Anticipated Start",
      "Actual / Anticipated Finish",
      "Remaining Duration as per Baseline (Days)",
      "Remaining Duration as per Finish (Days)",
      "Planned Complete (Percentage)",
      "Project Completion (Percentage)",
      "Remaining (Percentage)"
    ];

    let headerRow1 = [...staticHeaders]; // First row
    let headerRow2 = [...staticHeaders.map(() => "")]; // Second row

    let merges = [];

    let colIndex = staticHeaders.length;

    if (isShowMonths) {
      months.forEach((m) => {
        const mKey = formatKey(m.month);

        let monthCols = [];

        if (m.month === "Nov-2024 to Dec-2025") {
          monthCols = [
            { label: "BaseLine Planned Capex (INR Cr)", key: `${mKey}BaselineCost_Month` },
            { label: "BCWP (INR Cr)", key: `${mKey}ActualCost_Month` },
            { label: "Baseline Plan (Percentage)", key: `${mKey}BaselinePlanPct_Month` },
            { label: "BCWP till date (Percentage)", key: `${mKey}ActualPct_TillStatus` }
          ];
        } else {
          monthCols = [
            { label: "Weekly", key: `${mKey}Weekly` },
            { label: "Baseline Planned Capex (INR Cr)", key: `${mKey}BaselineCost_Month` },
            { label: "BCWP (INR Cr)", key: `${mKey}ActualCost_Month` },
            { label: "Revised Target (INR Cr)", key: `${mKey}RequiredTargetCost_Month` },
            { label: "Baseline Plan (Percentage)", key: `${mKey}BaselinePlanPct_Month` },
            { label: "Projected (Percentage)", key: `${mKey}ProjectedCompletionPct_Month` },
            { label: "Revised Target (Percentage)", key: `${mKey}RequiredTarget_Month` },
            { label: "BCWP till date (Percentage)", key: `${mKey}ActualPct_TillStatus` },
            { label: "Current Rate per day", key: `${mKey}CurrentRatePerDay` },
            { label: "Required Rate per day", key: `${mKey}RequiredRatePerDay` }
          ];
        }

        // First row me month name ek baar
        headerRow1.push(m.month);

        // Baaki cells empty rakho
        for (let i = 1; i < monthCols.length; i++) {
          headerRow1.push("");
        }

        // Second row me actual column labels
        monthCols.forEach(col => headerRow2.push(col.label));

        // Merge month header
        merges.push({
          s: { r: 0, c: colIndex },
          e: { r: 0, c: colIndex + monthCols.length - 1 }
        });

        colIndex += monthCols.length;
      });
    }

    // Data rows
    const dataRows = flatData.map(row => {
      let rowArr = [
        // row.resources?.join(", ") || "",
        row.activity || "",
        row.baselineCostTotal ?? "",
        row.revisedBaselineCostTotal ?? "",
        row.actualCostTotal ?? "",
        row.baselineStart || "",
        row.baselineFinish || "",
        row.actualStart || "",
        row.actualFinish || "",
        row.anticipatedStart || "",
        row.anticipatedFinish || "",
        row.remainingDurationAsPerBaseline ?? "",
        row.remainingDurationAsPerFinish ?? "",
        row.plannedComplete ?? "",
        row.projectCompletion ?? "",
        row.remaining ?? ""
      ];

      if (isShowMonths) {
        months.forEach((m) => {
          const mKey = formatKey(m.month);

          if (m.month === "Nov-2024 to Dec-2025") {
            rowArr.push(
              row[`${mKey}BaselineCost_Month`] ?? "",
              row[`${mKey}ActualCost_Month`] ?? "",
              row[`${mKey}BaselinePlanPct_Month`] ?? "",
              row[`${mKey}ActualPct_TillStatus`] ?? ""
            );
          } else {
            rowArr.push(
              row[`${mKey}Weekly`] ?? "",
              row[`${mKey}BaselineCost_Month`] ?? "",
              row[`${mKey}ActualCost_Month`] ?? "",
              row[`${mKey}RequiredTargetCost_Month`] ?? "",
              row[`${mKey}BaselinePlanPct_Month`] ?? "",
              row[`${mKey}ProjectedCompletionPct_Month`] ?? "",
              row[`${mKey}RequiredTarget_Month`] ?? "",
              row[`${mKey}ActualPct_TillStatus`] ?? "",
              row[`${mKey}CurrentRatePerDay`] ?? "",
              row[`${mKey}RequiredRatePerDay`] ?? ""
            );
          }
        });
      }

      return rowArr;
    });

    const worksheet = XLSX.utils.aoa_to_sheet([
      headerRow1,
      headerRow2,
      ...dataRows
    ]);

    worksheet["!merges"] = merges;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Schedule Data");

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const fileData = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });

    saveAs(fileData, "Capex View.xlsx");
  };

  useEffect(() => {
    if (tableColumns?.length) {
      console.log("tableColumns", tableColumns)
      setVisibleColumns(tableColumns.filter(col => col.type !== "month").map((col) => col.field));
    }
  }, [tableColumns]);

  useEffect(() => {
    if (months.length) {
      setVisibleMonths(months.map((m) => m.monthKey));
    }
  }, [months]);

  const filteredColumns = useMemo(() => {
    return tableColumns.filter((col) => {
      if (col.type !== "month") {
        return visibleColumns.includes(col.field);
      }

      if (col.type === "month") {
        return visibleMonths.includes(col.monthKey);
      }

    });
  }, [tableColumns, visibleColumns, visibleMonths]);

  console.log("filteredColumns", filteredColumns)

  const isExportDisabled =
    isLoading ||
    (isFiltering && lazyLoading) ||
    !initialData ||
    initialData.length === 0;

  return (
    <>

      <Grid container spacing={2} sx={{ width: "100%", boxSizing: "border-box", minHeight: "calc(100vh - 500px)", overflow: "hidden" }}>
        {/* Weekly drilldown popover */}
        <Popover
          open={weekPopover.open}
          anchorEl={weekPopover.anchorEl}
          onClose={closeWeekPopover}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{ sx: { p: 2, width: 800, borderRadius: 2 } }}
        >
          <Typography sx={{ fontFamily: "Outfit", fontWeight: 600, mb: 1 }}>
            Weekly Breakdown
          </Typography>

          {weekPopover.row && weekPopover.monthMeta ? (
            <>
              <Typography sx={{ fontFamily: "Outfit", fontSize: 13, color: "#6B7280", mb: 1 }}>
                <strong>{weekPopover.row.activity}</strong> • {weekPopover.monthMeta.month}
              </Typography>

              <Divider sx={{ mb: 1 }} />

              <ScheduleWeeklySection
                projectId={projectId}
                activityId={weekPopover.row?.id}
                monthMeta={weekPopover.monthMeta}
              />
            </>
          ) : (
            <Typography sx={{ fontFamily: "Outfit", fontSize: 13, color: "#6B7280" }}>No data.</Typography>
          )}
        </Popover>
        {/* resource drilldown popover */}
        <Popover
          open={resourcePopover.open}
          anchorEl={resourcePopover.anchorEl}
          onClose={closeResourcePopover}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{ sx: { p: 2, width: 700, borderRadius: 2 } }}
        >
          <Typography sx={{ fontFamily: "Outfit", fontWeight: 600, mb: 1 }}>
            Resources
          </Typography>

          {resourcePopover.row ? (
            <>
              <Typography sx={{ fontFamily: "Outfit", fontSize: 13, color: "#6B7280", mb: 1 }}>
                <strong>{resourcePopover.row.activity}</strong>
              </Typography>

              <Divider sx={{ mb: 1 }} />

              {(resourcePopover.row.resources?.length || 0) === 0 ? (
                <Typography sx={{ fontSize: 13, color: "#6B7280" }}>No resources mapped.</Typography>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                  <Box sx={{ display: "grid", gridTemplateColumns: "1.5fr .7fr .6fr .7fr .9fr .9fr .9fr .9fr", gap: 1 }}>
                    <Header title="Name" align="left" />
                    <Header title="Type" />
                    <Header title="UoM" />
                    <Header title="Units" />
                    <Header title="Baseline Cost" subtitle="(INR Cr.)" />
                    <Header title="BCWP" subtitle="(INR Cr.)" />
                    <Header title="Rem. Cost" subtitle="(INR Cr.)" />
                    <Header title="Work Completed" />
                  </Box>

                  {resourcePopover.row.resources.map((r, idx) => (
                    <Box
                      key={r.id || idx}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1.5fr .7fr .6fr .7fr .9fr .9fr .9fr 1fr",
                        gap: 1,
                        alignItems: "center",
                        py: 0.5,
                        px: 0.5,
                        borderRadius: 1,
                        "&:hover": { bgcolor: "rgba(0,0,0,0.03)" },
                      }}
                    >
                      <Typography sx={{ fontSize: 12, color: "#374151" }}>{r.resourceName ?? "—"}</Typography>
                      <Typography sx={{ fontSize: 12, textAlign: "center", color: "#6B7280" }}>
                        {r.resourceType ?? "—"}
                      </Typography>
                      <Typography sx={{ fontSize: 12, textAlign: "center", color: "#6B7280" }}>
                        {r.uom ?? "—"}
                      </Typography>
                      <Typography sx={{ fontSize: 12, textAlign: "right" }}>
                        {formatCurrency(r.units) ?? "—"}
                      </Typography>
                      <Typography sx={{ fontSize: 12, textAlign: "right" }}>
                        {formatCurrency(r.cost) ?? "—"}
                      </Typography>
                      <Typography sx={{ fontSize: 12, textAlign: "right" }}>
                        {formatCurrency(r.actualCost) ?? "—"}
                      </Typography>
                      <Typography sx={{ fontSize: 12, textAlign: "right" }}>
                        {formatCurrency(r.remainingCost) ?? "—"}
                      </Typography>
                      <Typography sx={{ fontSize: 12, textAlign: "center", color: "#6B7280" }}>
                        {r?.workCompleted ?? "—"}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </>
          ) : (
            <Typography sx={{ fontSize: 13, color: "#6B7280" }}>No data.</Typography>
          )}
        </Popover>

        <Grid size={12}>
          <Typography sx={{ fontSize: 15, lineHeight: "21px", fontWeight: 650, fontFamily: "Outfit", mb: 1 }}>
            {isShowMonths === true ? "Capex Tracker" : "Schedule Activities"}

            <Typography
              component="span"
              sx={{
                ml: 1,
                fontSize: 12,
                fontWeight: 400,
                color: "#9CA3AF",
                fontFamily: "Outfit",
                display: "inline",   // ✅ THIS fixes spacing
              }}
            >
              As on {scheduleDate?.scheduleUpdatedDate ? moment(scheduleDate?.scheduleUpdatedDate).format("DD MMM YYYY") : "N/A"}
            </Typography>
          </Typography>
        </Grid>
        {/* Header + Filters */}
        <Box
          sx={{
            mt: 0,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            width: "100%",
          }}
        >
          {/* {showDefination && */}
          <Grid size={12}>
            <Grid container spacing={1.5}>
              {abbreviations.map((item) => (
                <Grid size={{ md: 3 }}>
                  <AbbreviationBadge {...item} />
                </Grid>
              ))}
            </Grid>
          </Grid>
          {/* } */}
          {/* <Button
          variant="text"
          onClick={handleToggleDefination}
          sx={{
            fontFamily: "Outfit, sans-serif",
            textTransform: "none",
            fontWeight: 500,
          }}
        >
          {showDefination ? "Hide Definition" : "Show Definition"}
        </Button> */}
          {/* <Typography sx={{ fontSize: 18, fontWeight: 600, fontFamily: "Outfit", mb: 1.5 }}>
          {isShowMonths === true ? "Capex Tracker" : "Schedule Activities"}

          <Typography
            component="span"
            sx={{
              ml: 1,
              fontSize: 12,
              fontWeight: 400,
              color: "#9CA3AF",
              fontFamily: "Outfit",
              display: "inline",   // ✅ THIS fixes spacing
            }}
          >
            As on {moment("2026-02-13").format("DD MMM YYYY")}
          </Typography>
        </Typography> */}

          <Stack direction="row" spacing={0.75} alignItems={"center"}>
            {columnFiltering && <IconButton
              onClick={handleColumnOpen}
              size="small"
              sx={{ border: "1px solid #E2E8F0", borderRadius: "3px" }}
            >
              <ViewColumnIcon />
            </IconButton>}
            {Object.entries(filters).map(([key, value]) => {
              if (key === "projectId" || key === "wbsId" || key === "activityId" || !value) return null;

              return (
                <Chip
                  key={key}
                  label={
                    <Typography variant="body2">
                      <strong style={{ color: "#0E73F6" }}>{filterLabels[key] || key}:</strong>{" "}
                      <span style={{ fontWeight: 400, color: "#6B7280" }}>{value}</span>
                    </Typography>
                  }
                  sx={{
                    backgroundColor: "rgba(25, 118, 210, 0.08)",
                    border: 0,
                    borderRadius: "3px",
                  }}
                  variant="outlined"
                  onDelete={() => handleFilterDelete(key)}
                />
              );
            })}

            <IconButton aria-label="filter" size="small" ref={anchorRef} onClick={handleToggle} sx={{ border: "1px solid #E2E8F0", borderRadius: "3px" }}>
              <TuneOutlinedIcon fontSize="small" />
            </IconButton>

            <Popper
              open={open}
              anchorEl={anchorRef.current}
              placement="bottom-end"
              transition
              disablePortal={false}
              onClick={(e) => e.stopPropagation()}
              modifiers={[
                { name: "arrow", enabled: true, options: { element: arrowRef?.current } },
              ]}
              sx={{ zIndex: 100 }}
            >
              {({ TransitionProps }) => (
                <Grow {...TransitionProps}>
                  <Paper
                    sx={{
                      p: 2,
                      boxShadow: "none",
                      width: 300,
                      borderRadius: "3px",
                      border: "1px solid #E2E8F0",
                      position: "relative",
                    }}
                  >
                    <ArrowDropUpRoundedIcon
                      ref={arrowRef}
                      sx={{
                        position: "absolute",
                        top: "-33px",
                        right: "0px",
                        fontSize: "60px",
                        color: "white",
                        filter: "drop-shadow(0px -1px 1px rgba(0,0,0,0.15))",
                      }}
                    />
                    <ClickAwayListener onClickAway={handleClose}>
                      <Box>
                        {open && (
                          <Formik
                            initialValues={filters}
                            validationSchema={validationSchema}
                            onSubmit={handleApplyFilters}
                            enableReinitialize
                          >
                            {({ values, isSubmitting, setFieldValue, resetForm }) => (
                              <Form>
                                <Grid container spacing={2}>
                                  <Grid size={{ xs: 12, md: 12 }}>
                                    <Box sx={{ mb: 2 }}>
                                      <WbsDateFilters values={values} setFieldValue={setFieldValue} />
                                    </Box>

                                    <Typography sx={labelTextStyles}>WBS Selector</Typography>

                                    <WbsTreeView
                                      projectId={projectId}
                                      onSelect={(node) => {
                                        setFieldValue("wbsId", node.id);
                                        setFieldValue("wbsName", node.name);
                                      }}
                                      containerStyle={{ backgroundColor: "#fff", p: 0 }}
                                    />

                                    {values.wbsName && (
                                      <Typography sx={{ mt: 1, fontSize: 12, color: "#555" }}>
                                        Selected WBS: <strong>{values.wbsName}</strong>
                                      </Typography>
                                    )}
                                  </Grid>

                                  <Grid size={12}>
                                    <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                                      {isApplyFilter && (
                                        <Button
                                          onClick={() => handleReset(resetForm)}
                                          sx={{
                                            textTransform: "none",
                                            color: "#F15744",
                                            fontSize: "13px",
                                            lineHeight: "100%",
                                            fontFamily: "Outfit, sans-serif",
                                            borderRadius: "3px",
                                            fontWeight: 500,
                                            px: 2,
                                            py: 1,
                                          }}
                                        >
                                          Reset
                                        </Button>
                                      )}

                                      <Button
                                        type="submit"
                                        variant="contained"
                                        disabled={isSubmitting}
                                        sx={{
                                          backgroundColor: "#2563EB",
                                          borderRadius: "3px",
                                          textTransform: "none",
                                          fontWeight: 500,
                                          fontSize: "13px",
                                          lineHeight: "100%",
                                          fontFamily: "Outfit, sans-serif",
                                          color: "#FFFFFF",
                                          px: 2,
                                          py: 1,
                                        }}
                                      >
                                        Apply
                                      </Button>
                                    </Box>
                                  </Grid>
                                </Grid>
                              </Form>
                            )}
                          </Formik>
                        )}
                      </Box>
                    </ClickAwayListener>
                  </Paper>
                </Grow>
              )}
            </Popper>
            {exportButton &&
              <Button
                variant="contained"
                disabled={isExportDisabled}
                onClick={handleExport}
                sx={{
                  backgroundColor: "#2563EB",
                  borderRadius: "3px",
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: "13px",
                  lineHeight: "100%",
                  fontFamily: "Outfit, sans-serif",
                  color: "#FFFFFF",
                  px: 2,
                  py: 1
                }}
              >
                Export
              </Button>
            }
          </Stack>
        </Box>

        {/* Table */}
        {/* {showDefination &&
        <Grid size={12}>
          <Grid container spacing={1.5}>
            {abbreviations.map((item) => (
              <Grid size={{ md: 3 }}>
                <AbbreviationBadge {...item} />
              </Grid>
            ))}
          </Grid>
        </Grid>
      } */}

        <Grid size={12} sx={{ mt: 1, position: "relative", minHeight: "400px" }}>
          {(isLoading || (isFiltering && lazyLoading)) && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "rgba(255, 255, 255, 0.7)",
                zIndex: 10,
              }}
            >
              <CircularProgress />
            </Box>
          )}

          {(isError || lazyError) && (
            <Box sx={{ p: 4, textAlign: "center", color: "red" }}>
              <Typography variant="h6">Error loading data.</Typography>
            </Box>
          )}

          <Box
            sx={{
              opacity: isLoading || (isFiltering && lazyLoading) ? 0.5 : 1,
              pointerEvents: isLoading || (isFiltering && lazyLoading) ? "none" : "auto",
              transition: "opacity 0.2s ease",
            }}
          >
            {!isError && (
              <NestedTableComponent
                data={initialData}
                columns={filteredColumns}
                handleClick={handleClick}
                loaderId={loaderId}
                months={isShowMonths ? monthsWithSpan : []}
                isHeaderSticky={true}
              />
            )}
          </Box>
        </Grid>
      </Grid>
      <Popover
        open={Boolean(columnAnchor)}
        anchorEl={columnAnchor}
        onClose={handleColumnClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 2, width: 300 }}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>
            Select Columns
          </Typography>

          {tableColumns.filter((col) => col.type !== "month" && col.field !== "resources").map((col) => (
            <Box key={col.field} sx={{ display: "flex", alignItems: "center" }}>
              <Checkbox
                checked={visibleColumns.includes(col.field)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setVisibleColumns((prev) => [...prev, col.field]);
                  } else {
                    setVisibleColumns((prev) =>
                      prev.filter((item) => item !== col.field)
                    );
                  }
                }}
              />
              {/* {console.log("typeof", typeof col.name, col)} */}
              <Typography sx={{ fontSize: 13 }}>
                {col.type === "month" ? col.field : col.name.props.title}
              </Typography>
            </Box>
          ))}
          {isShowMonths &&
            <>
              <Typography sx={{ fontWeight: 600, mt: 2 }}>
                Months
              </Typography>

              {months.map((m) => {
                const mKey = m.monthKey || formatKey(m.month);

                return (
                  <Box key={mKey} sx={{ display: "flex", alignItems: "center" }}>
                    <Checkbox
                      checked={visibleMonths.includes(mKey)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setVisibleMonths((prev) => [...prev, mKey]);
                        } else {
                          setVisibleMonths((prev) =>
                            prev.filter((key) => key !== mKey)
                          );
                        }
                      }}
                    />
                    <Typography>{m.month}</Typography>
                  </Box>
                );
              })}
            </>
          }
        </Box>
      </Popover>
    </>
  );
};

export default ScheduleActivitiesSection;
